/**
 * dynasor の initGourmetHorizontalScroll から横移動の核だけを抽出。
 * 元: public/js/site.js / public/scss/_top.scss
 * 元commit: 39bd7cc838ffae6a35120d2b99779acb283e0e80
 *
 * FFDでは開始待機と入口・出口の抵抗を追加できるよう拡張。既定値は元の1:1対応。
 * wheel自体は横取りしない。区画の高さ = 固定前の余白 + sticky要素（未指定ならviewport）の高さ + 必要な縦移動距離。
 * 既存のLenisを使う場合は、初期化後に { lenis } を渡す。
 * GSAP / ScrollTrigger / Swiperは不要。装飾、表示切替、進捗バーは含まない。
 * 動的CSS変数2つとstickyの構造は元処理の移植。
 * 戻り値.refresh()で内容変更後に再計測、destroy()で解除できる。
 */
export function createHorizontalRange(distance, edgeWidth = 0, resistance = 1, startHold = 0) {
  distance = Math.max(distance, 0);
  startHold = distance > 0 ? Math.max(startHold, 0) : 0;
  const edge = Math.min(Math.max(edgeWidth, 0), distance / 2);
  const speed = 1 / Math.max(resistance, 1);
  // 端の速度から中央の1:1へ滑らかに接続。追加距離は端だけに割り当てる。
  const edgeScroll = 2 * edge / (1 + speed);
  const travelDistance = distance + 2 * (edgeScroll - edge);
  const scrollDistance = startHold + travelDistance;
  const edgePosition = (offset) => {
    const t = offset / edgeScroll;
    return edgeScroll * (speed * t + (1 - speed) * (t ** 3 - t ** 4 / 2));
  };
  const positionAt = (offset) => {
    offset = Math.min(Math.max(offset - startHold, 0), travelDistance);
    if (!edge) return offset;
    if (offset < edgeScroll) return edgePosition(offset);
    if (offset > travelDistance - edgeScroll) return distance - edgePosition(travelDistance - offset);
    return edge + offset - edgeScroll;
  };
  const offsetAt = (position) => {
    position = Math.min(Math.max(position, 0), distance);
    if (position === 0 || !edge) return startHold + position;
    if (position === distance) return scrollDistance;
    // 矢印操作だけで使用する逆変換。表示・カウンターと同じ曲線を使う。
    let low = startHold;
    let high = scrollDistance;
    for (let index = 0; index < 36; index++) {
      const middle = (low + high) / 2;
      if (positionAt(middle) < position) low = middle;
      else high = middle;
    }
    return (low + high) / 2;
  };
  return { distance, scrollDistance, positionAt, offsetAt };
}

// カードの左端で縦移動だけを消費する。時間やスクロール方向に依存しない。
export function addHorizontalHolds(range, positions, holdDistance) {
  if (range.distance <= 0 || holdDistance <= 0) return range;
  const stops = [...new Set(positions.filter((position) => Number.isFinite(position)
    && position >= 0 && position <= range.distance))].sort((a, b) => a - b);
  const holds = stops.map((position, index) => ({
    position, start: range.offsetAt(position) + index * holdDistance,
  }));
  return {
    distance: range.distance,
    scrollDistance: range.scrollDistance + holds.length * holdDistance,
    positionAt(offset) {
      let consumed = 0;
      for (const hold of holds) {
        if (offset < hold.start) break;
        if (offset <= hold.start + holdDistance) return hold.position;
        consumed += holdDistance;
      }
      return range.positionAt(offset - consumed);
    },
    // 矢印・タップは保持区間の入口へ移動する。
    offsetAt(position) {
      position = Math.max(0, Math.min(position, range.distance));
      return range.offsetAt(position) + stops.filter((stop) => stop < position).length * holdDistance;
    },
  };
}

export function initHorizontalScroll(root, { lenis, scroller = window, viewport, stickyElement, startOffset = () => 0, edgeWidth = () => 0, edgeResistance = 1, startHoldVh = 0, stickyTop, onUpdate, viewportWidth, holdPositions = () => [], holdDistance = () => 0 } = {}) {
  const track = root?.querySelector('[data-horizontal-track]');
  if (!track) return null;

  let range = createHorizontalRange(0);
  let frame = 0;
  let destroyed = false;
  let writtenPosition = 0;
  let scrollHeight;
  let offset = 0;
  const images = [...track.querySelectorAll('img')];

  const nativeScroll = () => scroller === window ? window.scrollY : scroller.scrollTop;
  const rootTop = () => root.getBoundingClientRect().top + nativeScroll() - (scroller === window ? 0 : scroller.getBoundingClientRect().top);
  const getState = () => {
    const scrollY = typeof lenis?.scroll === 'number' ? lenis.scroll : nativeScroll();
    return { position: range.positionAt(scrollY - rootTop() - offset), distance: range.distance };
  };
  const update = () => {
    frame = 0;
    const state = getState();
    const { position } = state;
    if (viewport) {
      viewport.scrollLeft = position;
      writtenPosition = viewport.scrollLeft;
    } else root.style.setProperty('--horizontal-track-x', `${(-position).toFixed(2)}px`);
    onUpdate?.(state);
  };

  const requestUpdate = () => {
    if (frame || destroyed) return;
    frame = window.requestAnimationFrame(update);
  };

  const refresh = () => {
    if (destroyed) return;
    // 元処理と同じく、開始位置の余白も横移動距離に含める。
    const maxDistance = Math.max(track.offsetLeft + track.scrollWidth - (viewportWidth?.() ?? viewport?.clientWidth ?? window.innerWidth), 0);
    range = addHorizontalHolds(
      createHorizontalRange(maxDistance, edgeWidth(), edgeResistance, window.innerHeight * startHoldVh / 100),
      holdPositions(), holdDistance(),
    );
    const inset = stickyTop?.() ?? 0;
    offset = startOffset() - inset;
    if (stickyTop) root.style.setProperty('--horizontal-sticky-top', `${inset}px`);
    const nextHeight = offset + inset + (stickyElement?.offsetHeight ?? window.innerHeight) + range.scrollDistance;
    if (scrollHeight !== nextHeight) {
      scrollHeight = nextHeight;
      root.style.setProperty('--horizontal-scroll-height', `${scrollHeight}px`);
      lenis?.resize();
    }
    if (viewport) {
      // リサイズ時のブラウザの位置補正を、バーの手動操作と誤認しない。
      window.cancelAnimationFrame(frame);
      update();
    } else requestUpdate();
  };

  const getScrollPosition = (position) => rootTop() + offset + range.offsetAt(position);
  const onHorizontalScroll = () => {
    if (destroyed || lenis?.isStopped || Math.abs(viewport.scrollLeft - writtenPosition) < .01) return;
    const target = getScrollPosition(viewport.scrollLeft);
    if (lenis) lenis.scrollTo(target, { immediate: true });
    else scroller.scrollTo({ top: target, behavior: 'instant' });
  };
  viewport?.addEventListener('scroll', onHorizontalScroll, { passive: true });
  if (lenis) {
    lenis.on('scroll', requestUpdate);
  } else {
    scroller.addEventListener('scroll', requestUpdate, { passive: true });
  }
  window.addEventListener('resize', refresh);
  window.addEventListener('load', refresh, { once: true });
  images.forEach((image) => {
    if (image.complete) return;
    image.addEventListener('load', refresh, { once: true });
    image.addEventListener('error', refresh, { once: true });
  });
  refresh();

  return {
    refresh,
    getState,
    getScrollPosition,
    destroy() {
      destroyed = true;
      window.cancelAnimationFrame(frame);
      if (lenis) lenis.off('scroll', requestUpdate);
      else scroller.removeEventListener('scroll', requestUpdate);
      viewport?.removeEventListener('scroll', onHorizontalScroll);
      window.removeEventListener('resize', refresh);
      window.removeEventListener('load', refresh);
      images.forEach((image) => {
        image.removeEventListener('load', refresh);
        image.removeEventListener('error', refresh);
      });
      root.style.removeProperty('--horizontal-scroll-height');
      root.style.removeProperty('--horizontal-track-x');
      if (stickyTop) root.style.removeProperty('--horizontal-sticky-top');
    },
  };
}
